import type { ReluxType } from '@/api/types'

/** Numbers are kept as strings while editing so a half-typed "2." is never lost. */
export type Num = string

export type ContactDraft = {
  key: string
  contactId?: string
  fullName: string
  company: string
  role: string
  email: string
  phone: string
  isPrimary: boolean
}

export type ExistingFitting = { key: string; type: string; watts: Num; qty: Num }

export type SpaceDraft = {
  clientId: string
  name: string
  spaceType: string
  lengthM: Num
  widthM: Num
  heightM: Num
  mountingHeightM: Num
  workingPlaneM: Num
  shape: 'rectangle' | 'irregular'
  targetLux: Num
  luxUndecided: boolean
  ceilingType: string
  finishCeiling: '' | 'light' | 'medium' | 'dark'
  finishWalls: '' | 'light' | 'medium' | 'dark'
  finishFloor: '' | 'light' | 'medium' | 'dark'
  environment: string[]
  racking: boolean
  rackingHeightM: Num
  aisleDirection: '' | 'length' | 'width'
  machinery: boolean
  mezzanine: boolean
  daylight: '' | 'none' | 'some' | 'lots'
  hoursPerDay: Num
  daysPerWeek: Num
  existingLighting: ExistingFitting[]
  controls: string[]
  emergency: '' | 'yes' | 'no' | 'not_sure'
  notes: string
  transcript: string
  /** Local photo file ids attached to this space. */
  fileIds: string[]
  /** Local voice note file ids. */
  voiceNoteIds: string[]
  /** Sports fields and courts (shown when the space looks like one). */
  sportsPoles?: string
  sportsPoleHeightM?: string
  sportsLayout?: string
  sportsLevel?: string
}

export type ItemDraft = {
  key: string
  productId?: string
  productName?: string
  freeText: string
  qty: Num
  spaceClientId: string
  notes: string
}

export type DocumentDraft = { fileId: string; category: string; spaceClientId?: string }

export type ReluxDraft = {
  clientUuid: string
  type: ReluxType
  isQuick: boolean
  /** Amendments only. */
  projectId?: string
  projectName?: string
  changeSummary: string
  followUpOwnerId: string
  project: {
    name: string
    siteAddress: string
    siteSuburb: string
    siteLat?: number
    siteLng?: number
    application: string
    buildingType: string
    projectType: string
    projectStage: string
    urgency: boolean
    urgencyReason: string
    requiredBy: string
    budgetBand: string
  }
  contacts: ContactDraft[]
  spaces: SpaceDraft[]
  products: { mode: 'cam_choose' | 'rep_products'; items: ItemDraft[]; alternatives: string[] }
  documents: DocumentDraft[]
  /** Photos and voice notes not tied to a space (Quick Relux, amendments). */
  generalFileIds: string[]
  generalVoiceIds: string[]
  notes: string
}

export const WIZARD_STEPS = [
  { key: 'project', label: 'Project' },
  { key: 'people', label: 'People' },
  { key: 'spaces', label: 'Spaces' },
  { key: 'products', label: 'Products' },
  { key: 'documents', label: 'Documents' },
  { key: 'review', label: 'Review' },
] as const

export type StepKey = (typeof WIZARD_STEPS)[number]['key']

export const uid = () => crypto.randomUUID()

export function newSpace(index: number): SpaceDraft {
  return {
    clientId: uid(),
    name: index === 0 ? 'Whole building' : `Space ${index + 1}`,
    spaceType: '',
    lengthM: '',
    widthM: '',
    heightM: '',
    mountingHeightM: '',
    workingPlaneM: '',
    shape: 'rectangle',
    targetLux: '',
    luxUndecided: false,
    ceilingType: '',
    finishCeiling: '',
    finishWalls: '',
    finishFloor: '',
    environment: [],
    racking: false,
    rackingHeightM: '',
    aisleDirection: '',
    machinery: false,
    mezzanine: false,
    daylight: '',
    hoursPerDay: '',
    daysPerWeek: '',
    existingLighting: [],
    controls: [],
    emergency: '',
    notes: '',
    transcript: '',
    fileIds: [],
    voiceNoteIds: [],
  }
}

export function newContact(isPrimary = false): ContactDraft {
  return { key: uid(), fullName: '', company: '', role: '', email: '', phone: '', isPrimary }
}

export function newDraft(options: { quick?: boolean; type?: ReluxType } = {}): ReluxDraft {
  return {
    clientUuid: uid(),
    type: options.type ?? 'new',
    isQuick: options.quick ?? false,
    changeSummary: '',
    followUpOwnerId: '',
    project: {
      name: '',
      siteAddress: '',
      siteSuburb: '',
      application: '',
      buildingType: '',
      projectType: '',
      projectStage: '',
      urgency: false,
      urgencyReason: '',
      requiredBy: '',
      budgetBand: '',
    },
    contacts: [],
    spaces: options.quick ? [] : [newSpace(0)],
    products: { mode: 'cam_choose', items: [], alternatives: [] },
    documents: [],
    generalFileIds: [],
    generalVoiceIds: [],
    notes: '',
  }
}

export function draftTitle(d: ReluxDraft) {
  if (d.type === 'amendment') return `Amendment: ${d.projectName ?? 'project'}`
  return d.project.name.trim() || 'Untitled Relux'
}
